import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, FileText, FolderOpen, Link2Off } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/NewButton';
import Error from '@/components/shared/Error';
import Loading from '@/components/shared/Loading';
import { DocumentSelector } from '@/components/document-selector/DocumentSelector';
import { type Document, useDocuments } from '@/hooks/useDocuments';
import type { DocumentSelection } from '@/types';
import type { DocketDocument, LinkedCrimsonDocument } from './schema';

interface DocketDocumentLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterGuid: string;
  document: DocketDocument | null;
  currentLink: LinkedCrimsonDocument | null;
  onLink: (file: LinkedCrimsonDocument) => void;
  onUnlink: () => void;
}

const DocketDocumentLinkDialog = ({
  open,
  onOpenChange,
  matterGuid,
  document,
  currentLink,
  onLink,
  onUnlink,
}: DocketDocumentLinkDialogProps) => {
  // 'search' = quick name search list, 'browse' = full folder + file browser.
  const [view, setView] = useState<'search' | 'browse'>('search');

  // Only fetch the matter's files while the simple search view is open.
  const { documents, isLoading, error } = useDocuments(
    open && view === 'search' ? matterGuid : undefined,
  );

  // Reset back to the search view whenever the dialog is closed.
  const handleOpenChange = (next: boolean) => {
    if (!next) setView('search');
    onOpenChange(next);
  };

  const handleSelect = (file: LinkedCrimsonDocument) => {
    onLink(file);
    handleOpenChange(false);
  };

  // The folder browser is built for multi-select; we reuse it as a single
  // picker by treating a row click as an immediate link + close.
  const noSelections = useMemo(() => new Map<string, DocumentSelection>(), []);
  const handlePickFromBrowser = (doc: Document) => {
    handleSelect({ guid: doc.guid, name: doc.displayName ?? doc.fileName });
  };

  useEffect(() => {
    if (open) setView('search');
  }, [open]);

  if (view === 'browse') {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 p-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b border-border px-6 py-4 pr-12">
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowLeft}
              text="Back"
              className="shrink-0"
              onClick={() => setView('search')}
            />
            <div className="min-w-0 space-y-1 text-left">
              <DialogTitle className="text-base">Link a file</DialogTitle>
              <DialogDescription>
                {document
                  ? `Browse folders to attach a file to "${document.title}".`
                  : 'Browse folders to attach a file to this docket document.'}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden">
            <DocumentSelector
              matterSlug={matterGuid}
              documentSelections={noSelections}
              onToggleSelection={handlePickFromBrowser}
              onClearSelection={() => {}}
              showUploadButton={false}
              className="h-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-6">
        <DialogHeader className="pb-4 pr-10">
          <DialogTitle className="text-base">Link a file</DialogTitle>
          <DialogDescription>
            {document
              ? `Attach an existing matter file to "${document.title}".`
              : 'Attach an existing matter file to this docket document.'}
          </DialogDescription>
        </DialogHeader>

        {currentLink && (
          <div className="mb-4 flex min-w-0 items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm text-foreground" title={currentLink.name}>
                {currentLink.name}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={Link2Off}
              text="Unlink"
              className="shrink-0"
              onClick={() => {
                onUnlink();
                onOpenChange(false);
              }}
            />
          </div>
        )}

        <Command className="rounded-md border border-border">
          <CommandInput placeholder="Search matter files by name..." />
          <CommandList className="max-h-[380px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loading text="Loading files" />
              </div>
            ) : error ? (
              <div className="p-4">
                <Error message="Could not load matter files." />
              </div>
            ) : (
              <>
                <CommandEmpty>No files match your search.</CommandEmpty>
                <CommandGroup className="p-2">
                  {documents.map((file) => {
                    const name = file.displayName ?? file.fileName;
                    const isCurrent = currentLink?.guid === file.guid;
                    return (
                      <CommandItem
                        key={file.id}
                        value={`${name} ${file.guid}`}
                        onSelect={() => handleSelect({ guid: file.guid, name })}
                        className="my-0.5 gap-3 px-3 py-2.5 text-sm"
                      >
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 truncate">{name}</span>
                        {isCurrent && (
                          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                            Linked
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>

        <div className="mt-4 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            icon={FolderOpen}
            text="Browse folders"
            tooltip="Browse folders and files"
            onClick={() => setView('browse')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocketDocumentLinkDialog;
